import { Schema } from 'effect';
import { defineMessageUnion } from 'foldkit/message';
import { Diagnostic, TextRange, CompletionItem, SemanticToken, HoverSource } from '@foldworks/text-intelligence';
export { CompletionItem, Diagnostic, SemanticToken, TextRange } from '@foldworks/text-intelligence';

var __defProp = Object.defineProperty;
var __export = (target, all) => {
  for (var name in all)
    __defProp(target, name, { get: all[name], enumerable: true });
};
var TextEdit = Schema.Struct({ from: Schema.Number, to: Schema.Number, insert: Schema.String });
var Selection = Schema.Struct({ anchor: Schema.Number, head: Schema.Number });
var Document = Schema.Struct({
  uri: Schema.String,
  session: Schema.Number,
  revision: Schema.Number,
  languageId: Schema.String,
  text: Schema.String,
  lineEnding: Schema.Literals(["lf", "crlf"])
});
var normalizeText = (text) => text.replace(/\r\n?/g, "\n");
var exportText = (document) => document.lineEnding === "crlf" ? document.text.replace(/\n/g, "\r\n") : document.text;
var validOffset = (text, offset) => Number.isInteger(offset) && offset >= 0 && offset <= text.length && !(offset > 0 && offset < text.length && /[\uD800-\uDBFF]/.test(text[offset - 1]) && /[\uDC00-\uDFFF]/.test(text[offset]));
var validSelection = (text, selection) => validOffset(text, selection.anchor) && validOffset(text, selection.head);
var applyEdits = (text, edits) => {
  let end = 0;
  const parts = [];
  for (const edit of edits) {
    if (!validOffset(text, edit.from) || !validOffset(text, edit.to) || edit.from < end || edit.to < edit.from || edit.insert.includes("\r")) {
      throw new Error("Edits must be ordered, nonoverlapping UTF-16 ranges with LF text.");
    }
    parts.push(text.slice(end, edit.from), edit.insert);
    end = edit.to;
  }
  return parts.join("") + text.slice(end);
};
var positionAt = (text, offset) => {
  if (!validOffset(text, offset)) throw new Error("Invalid document offset.");
  const prefix = text.slice(0, offset);
  return { line: prefix.split("\n").length - 1, character: offset - prefix.lastIndexOf("\n") - 1 };
};
var offsetAt = (text, position) => {
  const lines = text.split("\n");
  const line = lines[position.line];
  if (!Number.isInteger(position.line) || position.line < 0 || line === void 0 || !validOffset(line, position.character)) throw new Error("Invalid document position.");
  return lines.slice(0, position.line).reduce((sum, part) => sum + part.length + 1, 0) + position.character;
};
var DiagnosticBatch = Schema.Struct({
  uri: Schema.String,
  session: Schema.Number,
  revision: Schema.Number,
  languageId: Schema.String,
  source: Schema.String,
  diagnostics: Schema.Array(Diagnostic)
});
var validDiagnostics = (text, diagnostics) => diagnostics.every(
  (issue) => validOffset(text, issue.from) && validOffset(text, issue.to) && issue.from <= issue.to
);
var jsonDiagnostics = ({ text, languageId }) => {
  if (languageId !== "json") return [];
  try {
    JSON.parse(text);
    return [];
  } catch (error) {
    const message = error instanceof Error ? error.message : "Invalid JSON.";
    const position = /position (\d+)/i.exec(message);
    const line = /line (\d+) column (\d+)/i.exec(message);
    let from = /end of/i.test(message) ? text.length : 0;
    if (position) from = Number(position[1]);
    else if (line) {
      try {
        from = offsetAt(text, { line: Number(line[1]) - 1, character: Number(line[2]) - 1 });
      } catch {
        from = 0;
      }
    }
    if (!validOffset(text, from)) from = 0;
    let to = Math.min(text.length, from + 1);
    if (!validOffset(text, to)) to++;
    return [{ from, to, severity: "error", message }];
  }
};
var jsonValidator = { source: "json", validate: jsonDiagnostics };
var Options = Schema.Struct({
  readOnly: Schema.Boolean,
  lineNumbers: Schema.Boolean,
  lineWrapping: Schema.Boolean,
  tabSize: Schema.Number,
  theme: Schema.Literals(["light", "dark"]),
  /** `words` suggests words from the document; `host` shows only items the host offers. */
  suggestions: Schema.optional(Schema.Literals(["words", "host"]))
});
var Model = Schema.Struct({
  id: Schema.String,
  document: Document,
  selection: Selection,
  options: Options,
  diagnostics: Schema.Array(DiagnosticBatch),
  status: Schema.Literals(["Loading", "Ready", "Failed"]),
  error: Schema.String
});
var init = (config) => ({
  id: config.id,
  document: {
    uri: config.uri ?? `inmemory://foldworks/${encodeURIComponent(config.id)}`,
    session: 0,
    revision: 0,
    languageId: config.languageId ?? "text",
    text: normalizeText(config.text ?? ""),
    lineEnding: config.text?.includes("\r\n") ? "crlf" : "lf"
  },
  selection: { anchor: 0, head: 0 },
  options: {
    readOnly: config.readOnly ?? false,
    lineNumbers: config.lineNumbers ?? true,
    lineWrapping: config.lineWrapping ?? false,
    tabSize: Math.max(1, Math.min(8, Math.round(config.tabSize ?? 2) || 2)),
    theme: config.theme ?? "light",
    ...config.suggestions === void 0 ? {} : { suggestions: config.suggestions }
  },
  diagnostics: [],
  status: "Loading",
  error: ""
});
var DocumentVersion = Schema.Struct({ uri: Schema.String, session: Schema.Number, revision: Schema.Number });
var documentVersion = ({ uri, session, revision }) => ({
  uri,
  session,
  revision
});
var Operation = defineMessageUnion({
  ApplyEdits: {
    expected: DocumentVersion,
    edits: Schema.Array(TextEdit),
    selection: Schema.optional(Selection)
  },
  Select: { expected: DocumentVersion, selection: Selection },
  Focus: {},
  Undo: {},
  Redo: {},
  ReplaceDocument: { uri: Schema.String, text: Schema.String, languageId: Schema.String },
  SetLanguage: { languageId: Schema.String },
  SetOptions: Options.fields,
  SetDiagnostics: DiagnosticBatch.fields,
  /**
   * Paints token kinds over the built-in lexer, for the revision they were
   * computed for. Later edits carry them along until the next batch.
   */
  SetSemanticTokens: {
    uri: Schema.String,
    session: Schema.Number,
    revision: Schema.Number,
    tokens: Schema.Array(SemanticToken)
  },
  /** Offers items that replace `from`–`to`. Ignored unless the caret is in that range. */
  ShowCompletions: {
    expected: DocumentVersion,
    from: Schema.Number,
    to: Schema.Number,
    items: Schema.Array(CompletionItem)
  },
  /** Scrolls a range into view without moving focus or the selection. */
  Reveal: { expected: DocumentVersion, range: TextRange }
});
var Origin = Schema.Literals(["input", "external", "undo", "redo"]);
var OutMessage = defineMessageUnion({
  ChangedDocument: { document: Document, origin: Origin },
  ChangedSelection: { selection: Selection },
  RejectedOperation: { reason: Schema.String },
  /** Ctrl+Space or the Suggest button. Answer with `ShowCompletions`. */
  RequestedCompletion: { version: DocumentVersion, offset: Schema.Number },
  /** The pointer rested on a character, or Ctrl+Shift+Space asked about the caret. */
  Hovered: { version: DocumentVersion, offset: Schema.NullOr(Schema.Number), source: HoverSource }
});
var EditorSnapshot = Model;

export { DiagnosticBatch, Document, DocumentVersion, EditorSnapshot, Operation, Options, Origin, OutMessage, Selection, TextEdit, __export, applyEdits, documentVersion, exportText, init, jsonDiagnostics, jsonValidator, normalizeText, offsetAt, positionAt, validDiagnostics, validOffset, validSelection };
//# sourceMappingURL=chunk-3I3YYHFN.js.map
//# sourceMappingURL=chunk-3I3YYHFN.js.map